/**
 * Shared fetch helpers for the API client.
 * Domain modules (views, tasks, chat, etc.) use these for authenticated requests.
 */

import { shouldUseDevProxyRoute } from './dev-proxy';

/**
 * API base URL - uses environment variable in production, proxy in development.
 * Set VITE_API_URL in your .env or override via ?apiUrl or localStorage.
 */
export function resolveApiBaseUrl(): string {
  const devOverride = localStorage.getItem('danny_api_url_override');
  if (devOverride) {
    console.log('[Dev Mode] Using API override:', devOverride);
    return devOverride;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const queryApiUrl = urlParams.get('apiUrl');
  if (queryApiUrl) return queryApiUrl;
  return import.meta.env.VITE_API_URL || '';
}

export function getApiBase(): string {
  const base = resolveApiBaseUrl();

  if (
    shouldUseDevProxyRoute({
      isDev: import.meta.env.DEV,
      configuredBaseUrl: base,
      currentOrigin: window.location.origin,
    })
  ) {
    return '/api/v1';
  }

  return base ? `${base}/api/v1` : '/api/v1';
}

export function getApiKey(): string {
  return localStorage.getItem('danny_api_key') || '';
}

// When VITE_BYPASS_AUTH=true, send a test token so the API authenticates as Alice (seed user).
// Requires BYPASS_AUTH=true on the API server and seed data populated via `pnpm seed`.
const BYPASS_AUTH_HEADER =
  import.meta.env.VITE_BYPASS_AUTH === 'true'
    ? 'Bearer test:seed-alice-000000000:alice@example.com:member'
    : null;

/** Returns the correct auth headers for the current environment. */
export function getAuthHeaders(): Record<string, string> {
  return BYPASS_AUTH_HEADER
    ? { Authorization: BYPASS_AUTH_HEADER }
    : { 'x-api-key': getApiKey() };
}

export async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const apiBase = getApiBase();
  const fullUrl = `${apiBase}${endpoint}`;

  const authHeaders = getAuthHeaders();

  const response = await fetch(fullUrl, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Emit a custom event for 401 so AuthContext can clear session and redirect
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('danny:session-expired'));
    }

    // Emit a custom event for 402 so UI can show the upgrade modal
    if (response.status === 402) {
      window.dispatchEvent(new CustomEvent('danny:limit-reached', {
        detail: errorData,
      }));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- errorData is unknown JSON from server
    const message = (errorData as any).message || (errorData as any).error?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }

  if (response.status === 204) return {} as T;
  return response.json();
}
